import { Autocomplete, Checkbox, Input, Select, TextInput } from '@mantine/core';
import { IMaskInput } from 'react-imask';

import OverlaySection from '@/components/OverlaySection.tsx';
import { AddressAutocomplete } from '@/components/address-autocomplete.tsx';

import {
    ACADEMIC_TITLE_SUGGESTIONS,
    GENDER_OPTIONS,
    SALUTATION_OPTIONS,
} from '@/constants/patient-data-form.constants.ts';

import { usePatientDataForm } from '@/hooks/patient-data-form.hooks.ts';

import { AutoCompleteResult } from '@/models/AddressComplete.ts';
import { EditPatientFormData, PatientFormData } from '@/models/Patient.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';

import { patient_data_form_schema } from '@/schemas/PatientDataForm.schema.ts';

import { trimWhiteSpacesRecursively } from '@/utils/objectUtils.ts';

import Form from './Form.tsx';
import FormSection from './FormSection.tsx';
import { PatientCostCarrierSection } from './patient-cost-carrier-section.tsx';

type Props<T extends PatientFormData | EditPatientFormData> =
    | {
          isEdit: true;
          initialValues: EditPatientFormData;
          handleSubmit: (values: T) => void;
          pvsProvider: PVSProvider;
      }
    | {
          isEdit: false;
          initialValues?: PatientFormData;
          handleSubmit: (values: T) => void;
          pvsProvider?: never;
      };

export const PatientDataForm = ({
    initialValues,
    handleSubmit,
    isEdit,
    pvsProvider,
}: Props<PatientFormData | EditPatientFormData>) => {
    const {
        enrichedInitialValues,
        usedConfig,
        instanceIdOptions,
        practiceOptions,
        countryOptions,
        isDocCirrusProvider,
        isAddressRequired,
        handleInstanceIdSelect,
        handleValuesChange,
    } = usePatientDataForm(initialValues, isEdit, pvsProvider);

    return (
        <Form
            handleSubmit={(values) => {
                trimWhiteSpacesRecursively(values);
                handleSubmit(values);
            }}
            initialValues={enrichedInitialValues}
            isEdit={isEdit}
            validateInputOnChange
            schema={patient_data_form_schema}
            onValuesChange={handleValuesChange}
        >
            {(form, isEdit) => (
                <div className="flex flex-col gap-5 pt-4">
                    {!isEdit && instanceIdOptions.length > 1 && (
                        <OverlaySection className="items-start gap-2">
                            <Select
                                withAsterisk
                                label="Instanz"
                                data={instanceIdOptions}
                                className="w-1/2"
                                {...form.getInputProps('instance_id')}
                                onChange={(value: string | null) => {
                                    form.setFieldValue('instance_id', value ?? '');
                                    handleInstanceIdSelect(value ?? undefined);
                                }}
                            />
                        </OverlaySection>
                    )}
                    <OverlaySection className="items-start gap-2">
                        <FormSection
                            classNames={{
                                title: 'text-lg font-bold',
                                wrapper: 'w-full gap-y-3',
                            }}
                            title="Persönliche Informationen"
                        >
                            {isDocCirrusProvider && (
                                <Select
                                    label="Anrede"
                                    data={SALUTATION_OPTIONS}
                                    allowDeselect={false}
                                    key={form.key('salutation')}
                                    {...form.getInputProps('salutation')}
                                />
                            )}
                            {isDocCirrusProvider && (
                                <Select
                                    label="Geschlecht"
                                    placeholder="Bitte wählen..."
                                    data={GENDER_OPTIONS}
                                    allowDeselect={false}
                                    key={form.key('gender')}
                                    {...form.getInputProps('gender')}
                                />
                            )}
                            {isDocCirrusProvider && (
                                <Autocomplete
                                    label="Akad. Titel"
                                    placeholder="z.B. Dr."
                                    data={ACADEMIC_TITLE_SUGGESTIONS}
                                    clearable
                                    key={form.key('academic_title')}
                                    {...form.getInputProps('academic_title')}
                                />
                            )}
                            <TextInput
                                label="Vorname"
                                placeholder="Vorname"
                                key={form.key('first_name')}
                                {...form.getInputProps('first_name')}
                                withAsterisk
                            />
                            <TextInput
                                label="Nachname" // TODO: translation
                                placeholder="Nachname" // TODO: translation
                                key={form.key('last_name')}
                                {...form.getInputProps('last_name')}
                                withAsterisk
                            />

                            <Input.Wrapper label="Geburtsdatum" withAsterisk>
                                <Input
                                    component={IMaskInput}
                                    mask="00.00.0000"
                                    placeholder="TT.MM.JJJJ"
                                    key={form.key('date_of_birth')}
                                    {...form.getInputProps('date_of_birth')}
                                />
                            </Input.Wrapper>
                        </FormSection>
                        {isEdit && (
                            <Checkbox
                                label="PVS-Datenübermittlung zugestimmt"
                                disabled
                                {...form.getInputProps('pvs_approved', { type: 'checkbox' })}
                            />
                        )}
                    </OverlaySection>
                    <OverlaySection className="items-start gap-2">
                        <FormSection
                            classNames={{
                                title: 'pb-1 text-lg font-bold',
                                wrapper: 'w-full gap-y-3',
                            }}
                            title="Adresse"
                        >
                            <Select
                                label="Land"
                                placeholder="Land auswählen"
                                data={countryOptions}
                                searchable
                                key={form.key('country_code')}
                                {...form.getInputProps('country_code')}
                                onChange={(value: string | null) => form.setFieldValue('country_code', value ?? 'DE')}
                                withAsterisk
                            />
                            {form.values.country_code === 'DE' && (
                                <AddressAutocomplete
                                    form={form}
                                    withAsterisk={isAddressRequired}
                                    onSelect={(ac_result: AutoCompleteResult) => {
                                        form.setFieldValue(
                                            'street',
                                            `${ac_result.address.road ?? ac_result.address.name}`
                                        );
                                        if (ac_result.address.house_number) {
                                            form.setFieldValue('house_number', ac_result.address.house_number);
                                        }
                                        form.setFieldValue('postal_code', ac_result.address.postcode);
                                        form.setFieldValue('city', ac_result.address.city);
                                        form.setFieldValue(
                                            'country_code',
                                            ac_result.address.country_code.toUpperCase()
                                        );
                                    }}
                                />
                            )}
                            {form.values.country_code !== 'DE' && (
                                <TextInput
                                    label="Adresse"
                                    placeholder="Straße"
                                    key={form.key('street')}
                                    {...form.getInputProps('street')}
                                    withAsterisk={isAddressRequired}
                                />
                            )}
                            <TextInput
                                label="Hausnummer" // TODO: translation
                                placeholder="123"
                                key={form.key('house_number')}
                                {...form.getInputProps('house_number')}
                                withAsterisk={isAddressRequired}
                            />
                            {isDocCirrusProvider && (
                                <TextInput
                                    label="Adresszusatz"
                                    placeholder="Adresszusatz"
                                    key={form.key('addon')}
                                    {...form.getInputProps('addon')}
                                />
                            )}
                            <TextInput
                                label="Postleitzahl" // TODO: translation
                                placeholder="Postleitzahl" // TODO: translation
                                key={form.key('postal_code')}
                                {...form.getInputProps('postal_code')}
                                withAsterisk={isAddressRequired}
                            />
                            <TextInput
                                label="Stadt"
                                placeholder="Stadt"
                                key={form.key('city')}
                                {...form.getInputProps('city')}
                                withAsterisk={isAddressRequired}
                            />
                        </FormSection>
                    </OverlaySection>
                    <OverlaySection className="items-start gap-2">
                        <FormSection
                            classNames={{
                                title: 'pb-1 text-lg font-bold',
                                wrapper: 'w-full gap-y-3',
                                firstChild: 'flex-[0_0_100%]',
                            }}
                            title="Kontakt"
                        >
                            <TextInput
                                label="Email" // TODO: translation
                                placeholder="Email" // TODO: translation
                                key={form.key('email')}
                                {...form.getInputProps('email')}
                            />
                            <Input.Wrapper label="Mobilnummer" error={form.errors.phone}>
                                <Input
                                    component={IMaskInput}
                                    mask={/^[+]?[0-9\s\-()]*$/}
                                    placeholder="+490123456789"
                                    key={form.key('phone')}
                                    {...form.getInputProps('phone')}
                                />
                            </Input.Wrapper>
                            <Input.Wrapper label="Telefonnummer" error={form.errors.landline_phone}>
                                <Input
                                    component={IMaskInput}
                                    mask={/^[+]?[0-9\s\-()]*$/}
                                    placeholder="+49301234567"
                                    key={form.key('landline_phone')}
                                    {...form.getInputProps('landline_phone')}
                                />
                            </Input.Wrapper>
                        </FormSection>
                    </OverlaySection>
                    {!isEdit && ( // TODO: add edit support, consider that insurances are not editable (TBD), can only add a new one
                        <PatientCostCarrierSection
                            form={form}
                            usedConfig={usedConfig}
                            practiceOptions={practiceOptions}
                        />
                    )}
                </div>
            )}
        </Form>
    );
};
